import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface MySpectatorRequest {
  id: string;
  match_id: string;
  profile_id: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  payment_status: 'pending' | 'paid' | 'refunded' | 'expired';
  payment_deadline: string | null;
  created_at: string;
  match?: {
    id: string;
    sport: string;
    date: string;
    time: string;
    location: string;
    match_type: string;
    spectator_fee: number;
    created_by: string;
    creator?: {
      id: string;
      name: string;
      avatar: string | null;
    };
  };
}

// Get spectator requests for matches created by the current user
export function useCreatorSpectatorRequests() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['creator-spectator-requests', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];

      const { data, error } = await supabase
        .from('match_spectators')
        .select(`
          *,
          match:matches!match_spectators_match_id_fkey(
            id, sport, date, time, location, match_type, spectator_fee, created_by
          ),
          profile:profiles!match_spectators_profile_id_fkey(
            id, name, avatar, skill_level
          )
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Filter only spectator requests for matches created by current user
      return (data || []).filter(
        (s) => s.match?.created_by === profile.id
      );
    },
    enabled: !!profile?.id,
  });
}

// Get pending count for creator's spectator requests
export function useCreatorSpectatorRequestsCount() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['creator-spectator-requests-count', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return 0;

      // First get matches created by user
      const { data: matches, error: matchError } = await supabase
        .from('matches')
        .select('id')
        .eq('created_by', profile.id);

      if (matchError) throw matchError;
      if (!matches?.length) return 0;

      const matchIds = matches.map((m) => m.id);

      const { count, error } = await supabase
        .from('match_spectators')
        .select('*', { count: 'exact', head: true })
        .in('match_id', matchIds)
        .eq('status', 'pending');

      if (error) throw error;
      return count || 0;
    },
    enabled: !!profile?.id,
  });
}

/**
 * Same as useCreatorSpectatorRequestsCount but also returns the most
 * recent pending request's created_at, so the Header badge can hide
 * itself once the user has already viewed everything pending.
 */
export function useCreatorSpectatorRequestsSummary() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['creator-spectator-requests-summary', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return { count: 0, latestCreatedAt: null as string | null };

      const { data: matches, error: matchError } = await supabase
        .from('matches')
        .select('id')
        .eq('created_by', profile.id);

      if (matchError) throw matchError;
      if (!matches?.length) return { count: 0, latestCreatedAt: null };

      const matchIds = matches.map((m) => m.id);

      const { data, error } = await supabase
        .from('match_spectators')
        .select('created_at')
        .in('match_id', matchIds)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;
      const rows = data || [];
      return {
        count: rows.length,
        latestCreatedAt: rows[0]?.created_at ?? null,
      };
    },
    enabled: !!profile?.id,
  });
}
